import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/cards/Card';
import { Badge } from '../../components/common/Badge';
import { 
  LayoutDashboard, Lock, Map as MapIcon, Briefcase, GraduationCap, 
  ShieldCheck, Database, Server, Cpu, Globe, Share2, FileText, 
  Home, Search, UserCircle, CreditCard, Zap, GitBranch, MapPin, 
  FileBox, User
} from 'lucide-react';

export const SiteMap: React.FC = () => {

  // --- Data Structures ---

  const routeTree = [
    {
      category: "Public Access",
      color: "bg-blue-50 text-blue-700 border-blue-200",
      icon: Globe,
      description: "Accessible to all visitors without authentication.",
      routes: [
        { label: "Home / Landing", path: "/", icon: Home, status: "Live" },
        { label: "Browse Listings", path: "/browse", icon: Search, status: "Live", badge: "AI Powered" },
        { label: "Property Details", path: "/property/:id", icon: FileBox, status: "Dynamic", badge: "Live Data" },
        { label: "How It Works", path: "/how-it-works", icon: FileText, status: "Live" },
        { label: "Pricing", path: "/pricing", icon: CreditCard, status: "Live" },
        { label: "Contact Support", path: "/contact", icon: Share2, status: "Live" },
        { label: "Site Map", path: "/sitemap", icon: MapIcon, status: "Live" },
      ]
    },
    {
      category: "Authentication",
      color: "bg-slate-50 text-slate-700 border-slate-200",
      icon: Lock,
      description: "Secure entry points for user sessions.",
      routes: [
        { label: "Login", path: "/login", icon: UserCircle, status: "Secure" },
        { label: "Register", path: "/register", icon: UserCircle, status: "Secure" },
        { label: "Forgot Password", path: "/login", icon: Lock, status: "Secure" },
      ]
    },
    {
      category: "User Portals",
      color: "bg-purple-50 text-purple-700 border-purple-200",
      icon: LayoutDashboard,
      description: "Protected dashboards tailored to user roles.",
      routes: [
        { label: "Student Dashboard", path: "/student/dashboard", icon: GraduationCap, status: "Protected" },
        { label: "Agent Dashboard", path: "/agent/dashboard", icon: Briefcase, status: "Protected" },
        { label: "Landlord Portal", path: "/landlord/dashboard", icon: Home, status: "Protected" },
        { label: "Admin Console", path: "/admin/dashboard", icon: ShieldCheck, status: "Admin Only" },
        { label: "User Profile", path: "/profile", icon: User, status: "Protected" },
      ]
    }
  ];

  const systemArchitecture = {
    infrastructure: [
      { 
        title: "Supabase Auth", 
        desc: "JWT handling, Row Level Security (RLS) & Role management.", 
        icon: Lock,
        color: "text-green-600"
      },
      { 
        title: "PostgreSQL DB", 
        desc: "Relational data for Users, Properties, Bookings & Transactions.", 
        icon: Database,
        color: "text-blue-600"
      },
      { 
        title: "Edge Storage", 
        desc: "Image bucket 'property-images' with CDN caching.", 
        icon: Server,
        color: "text-orange-600"
      },
    ],
    aiLayer: [
      {
        type: "Reasoning & Search",
        model: "Gemini 2.5 Flash",
        usage: "Natural language processing for search queries and location grounding.",
        status: "Active"
      },
      {
        type: "Generative Vision",
        model: "Gemini 2.5 Flash Image",
        usage: "Dynamic hero image generation and visual analysis.",
        status: "Active"
      },
      {
        type: "Grounding Service",
        model: "Google Maps API",
        usage: "Real-time location data verification and address suggestions.",
        status: "Integrated"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-24">
      
      {/* Hero Header */}
      <div className="bg-white border-b border-gray-200 pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center p-3 bg-blue-50 rounded-xl mb-6 ring-1 ring-blue-100">
            <GitBranch className="w-8 h-8 text-[#2f5c96]" />
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-4">System Architecture</h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto">
            A transparent view of the application structure, route hierarchy, and underlying AI infrastructure.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* LEFT COLUMN: Route Map (The "Flesh") */}
          <div className="lg:col-span-7 space-y-10">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <MapIcon className="w-5 h-5 text-gray-400" />
                <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wider">Application Map</h2>
              </div>
              <span className="text-sm text-gray-500">v1.0.0</span>
            </div>

            <div className="space-y-12 relative">
              {/* Vertical Guide Line */}
              <div className="absolute left-[2.25rem] top-8 bottom-8 w-0.5 bg-gradient-to-b from-gray-200 via-gray-200 to-transparent -z-10"></div>

              {routeTree.map((section, idx) => (
                <div key={idx} className="relative">
                  {/* Category Header */}
                  <div className="flex items-start gap-6 mb-6 z-10 relative">
                    <div className={`w-20 h-20 rounded-2xl flex items-center justify-center shadow-sm border ${section.color} bg-white z-10`}>
                      <section.icon className="w-10 h-10" />
                    </div>
                    <div className="pt-2">
                      <h3 className="text-lg font-bold text-gray-900">{section.category}</h3>
                      <p className="text-sm text-gray-500 mt-1">{section.description}</p>
                    </div>
                  </div>

                  {/* Route List */}
                  <div className="ml-[2.25rem] pl-8 space-y-3">
                    {section.routes.map((route, rIdx) => (
                      <Link 
                        key={rIdx} 
                        to={route.path.includes(':') ? '#' : route.path}
                        className={`group flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-[#2f5c96] hover:shadow-md transition-all relative ${route.path.includes(':') ? 'cursor-default' : ''}`}
                      >
                        {/* Horizontal Connector */}
                        <div className="absolute -left-8 top-1/2 w-8 h-0.5 bg-gray-200 group-hover:bg-[#2f5c96] transition-colors"></div>
                        
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                            <route.icon className="w-5 h-5 text-gray-400 group-hover:text-[#2f5c96]" />
                          </div>
                          <div>
                            <span className="font-bold text-gray-700 group-hover:text-gray-900 block">{route.label}</span>
                            <span className="text-xs text-gray-400 font-mono">{route.path}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {route.badge && <Badge color="blue" className="text-[10px] hidden sm:inline-flex">{route.badge}</Badge>}
                          <span className={`text-xs px-2.5 py-1 rounded-full font-bold border ${
                            route.status === 'Live' ? 'bg-green-50 text-green-700 border-green-200' : 
                            route.status === 'Protected' ? 'bg-purple-50 text-purple-700 border-purple-200' : 
                            route.status === 'Dynamic' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                            'bg-slate-100 text-slate-600 border-slate-200'
                          }`}>
                            {route.status}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT COLUMN: Infrastructure & AI (The "Bones") */}
          <div className="lg:col-span-5 space-y-12">
            
            {/* Tech Stack Block */}
            <div>
              <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
                <Server className="w-5 h-5 text-gray-400" />
                <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wider">Tech Stack</h2>
              </div>
              
              <div className="grid gap-4">
                {systemArchitecture.infrastructure.map((item, idx) => (
                  <Card key={idx} className="p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
                    <div className={`p-3 bg-gray-50 rounded-full ${item.color}`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">{item.title}</h4>
                      <p className="text-xs text-gray-500 leading-relaxed mt-1">{item.desc}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* AI Integration Block */}
            <div className="bg-gradient-to-b from-[#2f5c96]/5 to-transparent p-6 rounded-2xl border border-[#2f5c96]/10">
              <div className="flex items-center gap-2 mb-6">
                <Cpu className="w-5 h-5 text-[#2f5c96]" />
                <h2 className="text-xl font-bold text-[#2f5c96] uppercase tracking-wider">AI Layer</h2>
              </div>

              <div className="space-y-4">
                {systemArchitecture.aiLayer.map((ai, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-[#c19cca]" />
                        <span className="font-bold text-gray-800 text-sm">{ai.type}</span>
                      </div>
                      <span className="text-[10px] font-mono bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{ai.model}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3 leading-relaxed">{ai.usage}</p>
                    <div className="flex items-center gap-2 text-[10px] text-green-600 font-bold uppercase tracking-wide">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                      {ai.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Roadmap / Stats */}
            <div className="bg-slate-900 rounded-2xl p-8 text-white shadow-xl">
              <h3 className="font-bold mb-6 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#c19cca]"/> Coverage
              </h3>
              <div className="grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-3xl font-extrabold text-[#c19cca] mb-1">5+</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Provinces</div>
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-[#c19cca] mb-1">20+</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Universities</div>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-slate-800">
                <p className="text-xs text-slate-500 text-center">
                  System architecture diagram generated dynamically.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};